import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import ItemCard from "@/components/ItemCard";
import CartBar from "@/components/CartBar";
import { useOrderMenu } from "@/hooks/useOrderMenu";
import { catSlug } from "@/utils/menu";

export default function CategoryPage() {
  const { slug } = useParams();
  const { menu, accepting } = useOrderMenu();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = menu?.categories.find((c) => catSlug(c) === slug);
  const idx = menu ? menu.categories.indexOf(category) : -1;
  const prev = idx > 0 ? menu.categories[idx - 1] : null;
  const next = menu && idx >= 0 && idx < menu.categories.length - 1 ? menu.categories[idx + 1] : null;
  const items = menu && category ? menu.items.filter((i) => i.category === category) : [];

  return (
    <div className="min-h-screen pt-16 pb-32" data-testid="category-page">
      <header className="max-w-7xl mx-auto px-6 pt-10 pb-8 border-b-2 border-zinc-800">
        <Link to="/order" className="inline-flex items-center gap-2 border-2 border-zinc-700 bg-white hover:border-ember px-6 py-3.5 font-mono font-bold text-sm tracking-wide uppercase text-ink hover:text-ember transition-colors duration-300 mb-6" data-testid="category-back">
          <ArrowLeft className="w-5 h-5" /> All categories
        </Link>
        <h1 className="font-display text-6xl sm:text-7xl leading-none uppercase text-gold-shine" data-testid="category-heading">
          {category || "…"}
        </h1>
        {category && (
          <>
            <p className="mt-3 font-mono text-sm tracking-[0.2em] uppercase text-zinc-500">
              {items.length} item{items.length > 1 ? "s" : ""} · everything goes in the same cart
            </p>
            <p className="mt-2 font-mono text-sm font-bold tracking-[0.15em] uppercase text-ember" data-testid="category-pickup-note">
              Pickup only — delivery via Uber Eats, DoorDash or Menulog
            </p>
          </>
        )}
      </header>

      {!accepting && (
        <div className="max-w-7xl mx-auto px-6 mt-6" data-testid="ordering-paused-banner">
          <p className="border border-ember bg-ember/10 text-ember font-mono text-xs tracking-[0.15em] uppercase px-4 py-3">
            Online ordering is paused right now — please call (03) 5024 5570 to order
          </p>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 pt-10">
        {!menu && <p className="font-mono text-sm text-zinc-500">Loading menu…</p>}
        {menu && !category && (
          <div className="border-2 border-zinc-700 bg-white p-10 text-center" data-testid="category-not-found">
            <p className="text-zinc-500 mb-6">That category doesn't exist.</p>
            <Link to="/order" className="inline-block bg-ember hover:bg-ember-hover text-white font-bold px-8 py-3 text-sm tracking-wide uppercase transition-colors duration-300">
              Browse categories
            </Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <ItemCard key={i.id} item={i} extras={menu.extras?.[category] || []} allItems={menu.items} />
          ))}
        </div>

        {category && (prev || next) && (
          <div className="mt-14 flex items-center justify-between gap-4" data-testid="category-pager">
            {prev ? (
              <Link to={`/order/c/${catSlug(prev)}`} data-testid="category-prev" className="group inline-flex items-center gap-2 border-2 border-zinc-700 bg-white hover:border-ember px-5 py-3 font-mono font-bold text-sm tracking-wide uppercase text-ink hover:text-ember transition-colors duration-300">
                <ArrowLeft className="w-4 h-4" /> {prev}
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/order/c/${catSlug(next)}`} data-testid="category-next" className="group inline-flex items-center gap-2 border-2 border-zinc-700 bg-white hover:border-ember px-5 py-3 font-mono font-bold text-sm tracking-wide uppercase text-ink hover:text-ember transition-colors duration-300">
                {next} <ArrowRight className="w-4 h-4" />
              </Link>
            ) : <span />}
          </div>
        )}
      </main>

      <CartBar />
    </div>
  );
}
